import React from 'react';
import { arrayOf, number, oneOfType, shape, string } from 'prop-types';
import * as R from 'ramda';
import { Box, Typography, useTheme } from '@mui/material';

// Components
import { ProductIndicatorBadge } from 'common';

// Utilities
import { format, isEqual, parseISO } from 'date-fns/fp';
import {
  NO_END_DATE,
  SUPPLIER_AUTHORIZATION_TYPE_OPTIONS,
} from 'feature/loading-management/utils/const';

const Summary = props => {
  const theme = useTheme();

  const { authorizationBy, inventoryOwner, location, params, product, selectedProducts, supplier } =
    props;

  const { authorizationNumber, authorizationType, authorizedVolume, endDate, renewalVolume } =
    params;

  const { label: type } = SUPPLIER_AUTHORIZATION_TYPE_OPTIONS.find(
    option => option.value === authorizationType
  );

  return (
    <Box bgcolor="surface.medium">
      <Typography
        sx={{
          color: 'primary.dark',
          fontWeight: 'bold',
          height: 44,
          px: 2,
          py: 1.5,
        }}
        variant="subtitle1"
      >
        Summary
      </Typography>

      <Typography
        sx={{
          backgroundColor: 'graphic.light',
          color: 'text.secondary',
          fontWeight: 'bold',
          height: 32,
          px: 2,
          py: 1,
        }}
        variant="body1"
      >
        1. Details
      </Typography>

      <Box
        sx={{
          alignItems: 'center',
          borderBottom: `solid 1px ${theme.palette.graphic.light}`,
          display: 'flex',
          justifyContent: 'space-between',
          p: 2,
        }}
      >
        <Typography sx={{ color: 'text.secondary' }} variant="body1">
          Affiliate
        </Typography>
        <Typography sx={{ color: 'text.primary' }} variant="body1">
          {inventoryOwner.name}
        </Typography>
      </Box>
      <Box
        sx={{
          alignItems: 'center',
          borderBottom: `solid 1px ${theme.palette.graphic.light}`,
          display: 'flex',
          justifyContent: 'space-between',
          p: 2,
        }}
      >
        <Typography sx={{ color: 'text.secondary' }} variant="body1">
          Supplier
        </Typography>
        <Typography sx={{ color: 'text.primary' }} variant="body1">
          {supplier.nameShort}
        </Typography>
      </Box>
      <Box
        sx={{
          alignItems: 'center',
          borderBottom: `solid 1px ${theme.palette.graphic.light}`,
          display: 'flex',
          justifyContent: 'space-between',
          p: 2,
        }}
      >
        <Typography sx={{ color: 'text.secondary' }} variant="body1">
          {authorizationBy === 'location' ? 'Location' : 'Product'}
        </Typography>
        <Typography sx={{ color: 'text.primary' }} variant="body1">
          {authorizationBy === 'location' ? location.nameShort : product.nameShort}
        </Typography>
      </Box>
      <Box
        sx={{
          alignItems: 'center',
          borderBottom: `solid 1px ${theme.palette.graphic.light}`,
          display: 'flex',
          justifyContent: 'space-between',
          p: 2,
        }}
      >
        <Typography sx={{ color: 'text.secondary' }} variant="body1">
          End Date
        </Typography>
        <Typography sx={{ color: 'text.primary' }} variant="body1">
          {isEqual(parseISO(endDate), NO_END_DATE)
            ? 'No End Date'
            : format('MMMM d, yyyy', parseISO(endDate))}
        </Typography>
      </Box>
      {authorizationNumber && (
        <Box
          sx={{
            alignItems: 'center',
            borderBottom: `solid 1px ${theme.palette.graphic.light}`,
            display: 'flex',
            justifyContent: 'space-between',
            p: 2,
          }}
        >
          <Typography sx={{ color: 'text.secondary' }} variant="body1">
            Authorization No.
          </Typography>
          <Typography sx={{ color: 'text.primary' }} variant="body1">
            {authorizationNumber}
          </Typography>
        </Box>
      )}
      <Box
        sx={{
          alignItems: 'center',
          borderBottom: `solid 1px ${theme.palette.graphic.light}`,
          display: 'flex',
          justifyContent: 'space-between',
          p: 2,
        }}
      >
        <Typography sx={{ color: 'text.secondary' }} variant="body1">
          Type
        </Typography>
        <Typography sx={{ color: 'text.primary' }} variant="body1">
          {type}
        </Typography>
      </Box>
      {authorizedVolume && (
        <Box
          sx={{
            alignItems: 'center',
            borderBottom: `solid 1px ${theme.palette.graphic.light}`,
            display: 'flex',
            justifyContent: 'space-between',
            p: 2,
          }}
        >
          <Typography sx={{ color: 'text.secondary' }} variant="body1">
            Authorized Vol (Gallons)
          </Typography>
          <Typography sx={{ color: 'text.primary' }} variant="body1">
            {authorizedVolume}
          </Typography>
        </Box>
      )}
      {renewalVolume && (
        <Box
          sx={{
            alignItems: 'center',
            borderBottom: `solid 1px ${theme.palette.graphic.light}`,
            display: 'flex',
            justifyContent: 'space-between',
            p: 2,
          }}
        >
          <Typography sx={{ color: 'text.secondary' }} variant="body1">
            Renewal Vol (Gallons)
          </Typography>
          <Typography sx={{ color: 'text.primary' }} variant="body1">
            {renewalVolume}
          </Typography>
        </Box>
      )}

      <Typography
        sx={{
          backgroundColor: 'graphic.light',
          color: 'text.secondary',
          fontWeight: 'bold',
          height: 32,
          px: 2,
          py: 1,
        }}
        variant="body1"
      >
        {`2. ${authorizationBy === 'location' ? 'Products' : 'Locations'}`}
      </Typography>

      {R.map(
        ({ label, proprietary, value }) => (
          <Box
            key={value}
            sx={{
              alignItems: 'center',
              borderBottom: `solid 1px ${theme.palette.graphic.light}`,
              display: 'flex',
              justifyContent: 'space-between',
              p: 2,
            }}
          >
            <Typography sx={{ color: 'text.primary' }} variant="body1">
              {label}
            </Typography>
            <ProductIndicatorBadge proprietary={proprietary} />
          </Box>
        ),
        selectedProducts
      )}
    </Box>
  );
};

Summary.propTypes = {
  authorizationBy: string.isRequired,
  inventoryOwner: shape({
    companyCustomerIdName: string,
  }).isRequired,
  location: shape({
    nameShort: string,
  }),
  params: shape({
    authorizationNumber: oneOfType([number, string]),
    authorizationType: string,
    authorizedVolume: oneOfType([number, string]),
    endDate: string,
    renewalVolume: oneOfType([number, string]),
  }).isRequired,
  product: shape({
    nameShort: string,
  }),
  selectedProducts: arrayOf(shape).isRequired,
  supplier: shape({
    nameShort: string,
  }).isRequired,
};

Summary.defaultProps = {
  location: null,
  product: null,
};

export default Summary;
